import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { getCountryData, getAvailableCountries, DEFAULT_COUNTRY } from '../data/sosNumbers';
import CountrySelector from '../components/sos/CountrySelector';
import SOSButton from '../components/sos/SOSButton';
import MoreHelpAccordion from '../components/sos/MoreHelpAccordion';
import Navbar from '../components/layout/Navbar';

/**
 * @page SOSPage
 * @description Página dedicada /sos con números de emergencia por país
 * CRÍTICO: Esta página puede salvar vidas - mantener simple y confiable
 */

const SOSPage: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [countryData, setCountryData] = useState(getCountryData(DEFAULT_COUNTRY));

  // Analytics tracking
  const handleSOSClick = (service: string, country: string, location: string) => {
    // GA4/Matomo event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sos_click', {
        country: country,
        service: service,
        location: location
      });
    }

    // Matomo tracking (si está disponible)
    if (typeof window !== 'undefined' && (window as any)._paq) {
      (window as any)._paq.push(['trackEvent', 'SOS', 'Click', `${country}-${service}-${location}`]);
    }

    console.log('SOS Click:', { service, country, location });
  };

  // Cambiar país
  const handleCountryChange = (countryId: string) => {
    setSelectedCountry(countryId);
    const newCountryData = getCountryData(countryId);
    setCountryData(newCountryData);

    // Analytics para cambio de país
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sos_country_change', {
        country: countryId,
        location: 'sos_page'
      });
    }
  };

  // SEO y meta tags
  useEffect(() => {
    document.title = 'Números de Emergencia por país | NFCores';
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Números oficiales de emergencia para Chile, México, Argentina, Colombia y Perú. Acceso rápido a servicios de ambulancia, bomberos y policía.');
    }
  }, []);

  if (!countryData) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Navbar de navegación principal */}
        <Navbar />

        <div className="flex items-center justify-center pt-16 min-h-mobile-screen">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900">Error al cargar datos</h1>
            <p className="text-gray-600 mt-2">No se pudieron cargar los números de emergencia.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar de navegación principal */}
      <Navbar />

      {/* Header - con padding superior para compensar navbar fijo */}
      <div className="bg-white shadow-sm pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Números de Emergencia por país
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              NFCORES no es un servicio de emergencias. Te conectamos con números oficiales para pedir ayuda inmediata.
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer legal permanente */}
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                <strong>Importante:</strong> NFCORES no presta atención médica ni policial. Las llamadas se dirigen a servicios oficiales.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selector de país */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <CountrySelector
            selectedCountry={selectedCountry}
            onCountryChange={handleCountryChange}
          />
        </div>

        {/* Contenido del país seleccionado */}
        <div className="space-y-8">
          {/* Botones principales */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Emergencias principales - {countryData.nombre}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {countryData.botonesPrincipales.map((service) => (
                <SOSButton
                  key={service.numero}
                  service={service}
                  country={countryData.id}
                  location="sos_page"
                  onSOSClick={handleSOSClick}
                  size="large"
                  showDescription={true}
                />
              ))}
            </div>
          </div>

          {/* Acordeón de más ayudas */}
          <MoreHelpAccordion
            country={countryData}
            onSOSClick={handleSOSClick}
          />

          {/* Notas del país */}
          {countryData.notas && countryData.notas.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">
                Notas importantes para {countryData.nombre}
              </h3>
              <ul className="space-y-2">
                {countryData.notas.map((nota, index) => (
                  <li key={index} className="text-blue-800 flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    {nota}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Información adicional */}
          <div className="bg-gray-100 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Información adicional
            </h3>
            <div className="space-y-2 text-gray-700">
              <p>• Si estás en riesgo vital, llama inmediatamente al número de emergencias de tu país.</p>
              <p>• El 112 puede funcionar en algunos móviles por estándar GSM, pero no reemplaza los números nacionales oficiales.</p>
              <p>• Estos números están disponibles 24 horas, 7 días a la semana.</p>
              <p>• En caso de estar en otro país no listado, contacta a las autoridades locales o tu embajada.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fallback sin JavaScript - CRÍTICO */}
      <noscript>
        <div className="bg-red-600 text-white p-8">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">EMERGENCIAS - SIN JAVASCRIPT</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white text-red-600 p-4 rounded">
                <h3 className="font-bold">CHILE</h3>
                <a href="tel:131" className="block text-lg font-bold">131 - SAMU</a>
                <a href="tel:132" className="block text-lg font-bold">132 - Bomberos</a>
                <a href="tel:133" className="block text-lg font-bold">133 - Carabineros</a>
              </div>
              <div className="bg-white text-red-600 p-4 rounded">
                <h3 className="font-bold">MÉXICO/ARGENTINA</h3>
                <a href="tel:911" className="block text-lg font-bold">911 - Emergencias</a>
              </div>
              <div className="bg-white text-red-600 p-4 rounded">
                <h3 className="font-bold">COLOMBIA</h3>
                <a href="tel:123" className="block text-lg font-bold">123 - Emergencias</a>
              </div>
            </div>
            <p className="text-sm">
              NFCORES no presta atención médica ni policial. Las llamadas se dirigen a servicios oficiales.
            </p>
          </div>
        </div>
      </noscript>
    </div>
  );
};

export default SOSPage;
